import { hash } from '@ember/helper';
import { set } from '@ember/object';
import Component from '@glimmer/component';
import type { WithBoundArgs } from '@glint/template';
import SelectableButtonGroupItemComponent from './selectable-button-group/item.gts';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    selected?: string;
    onChange: (value: string) => void;
    xs?: boolean;
    vertical?: boolean;
    centered?: boolean;
    columns?: string;
    keepSelected?: boolean;
    selectedColor?: string;
    onSelectedUpdate?: (value: string) => void;
    shouldConfirmSelection?: boolean;
    confirmationTitle?: string;
    confirmationText?: string;
    disabled?: boolean;
    normalCase?: boolean;
  };
  Blocks: {
    default: [
      {
        Item: WithBoundArgs<
          typeof SelectableButtonGroupItemComponent,
          | 'onChange'
          | 'keepSelected'
          | 'selectedValue'
          | 'selectedColor'
          | 'onSelectedUpdate'
          | 'xs'
          | 'shouldConfirmSelection'
          | 'confirmationTitle'
          | 'confirmationText'
          | 'normalCase'
        >;
      }
    ];
  };
}

/**
 * A media query cannot add the FUI `vertical` class. On small screens, these
 * classes copy the rules of `.ui.basic.vertical.buttons` instead.
 */
const SMALL_SCREEN_VERTICAL_STYLE = [
  'max-sm:[&.ui.buttons]:[flex-direction:column]',
  'max-sm:[&.ui.buttons>.button]:[display:flex]',
  'max-sm:[&.ui.buttons>.button]:[justify-content:center]',
  'max-sm:[&.ui.buttons>.button]:[width:auto]',
  'max-sm:[&.ui.buttons>.button]:[margin:0]',
  'max-sm:[&.ui.buttons>.button]:[border-radius:0]',
  'max-sm:[&.ui.buttons>.button]:[border-left:none]',
  'max-sm:[&.ui.buttons>.button]:[border-top:1px_solid_rgba(34,36,38,0.15)]',
  'max-sm:[&.ui.buttons>.button:first-child]:[border-top-width:0]',
  'max-sm:[&.ui.buttons>.button:first-child]:[border-radius:0.28571429rem_0.28571429rem_0_0]',
  'max-sm:[&.ui.buttons>.button:last-child]:[border-radius:0_0_0.28571429rem_0.28571429rem]'
].join(' ');

export default class SelectableButtonGroupComponent extends Component<Signature> {
  selected = this.args.selected;

  onChange = (value: string) => {
    set(this, 'selected', value);
    this.args.onChange?.(value);
  };

  get layoutStyle() {
    if (this.args.xs) return '';
    if (this.args.vertical) return 'vertical';

    return SMALL_SCREEN_VERTICAL_STYLE;
  }

  <template>
    <div class='ui grid {{if @centered "centered"}}'>
      <div class='mx-0 my-4' ...attributes>
        {{! prettier-ignore }}
        <div
          class='{{this.layoutStyle}}
            {{@columns}}
            ui basic buttons fluid'
        >
          {{! @glint-expect-error }}
          {{yield (hash Item=(component SelectableButtonGroupItemComponent onChange=this.onChange keepSelected=@keepSelected selectedValue=this.selected selectedColor=@selectedColor onSelectedUpdate=@onSelectedUpdate xs=@xs shouldConfirmSelection=@shouldConfirmSelection confirmationTitle=@confirmationTitle confirmationText=@confirmationText isReadOnly=@disabled normalCase=@normalCase) ) }}
        </div>
      </div>
    </div>
  </template>
}
